import { COVERAGE_CATALOG, COVERAGE_LEVELS, coverageIncluded } from '../models/coverage';
import { LineOfBusiness, Policy, ProtoDb } from '../models/domain.models';
import { PolicyExtras, PolicyPerson, RiskItem } from '../models/views.models';
import { hashOf } from './commission-math';

const SUM_FACTOR: Record<LineOfBusiness, number> = {
  auto: 55, home: 420, commerce: 380, liability: 900, 'personal-accident': 1200, life: 2000,
};
const FLEET_VEHICLES = ['Ford Transit 2021', 'Renault Kangoo 2020', 'Fiat Fiorino 2019', 'Volkswagen Saveiro 2022'];
const BENEFICIARIES = ['Lucía Ferreyra', 'Matías Duarte', 'Carolina Benítez', 'Ramiro Olmos'];
const DAMAGE_COVERAGES = new Set(['Daños parciales', 'Robo parcial', 'Incendio parcial', 'Destrucción total', 'Granizo', 'Cristales']);
const ITEM_DESCRIPTION: Record<LineOfBusiness, string> = {
  auto: 'Vehículo asegurado', home: 'Vivienda asegurada', commerce: 'Local comercial asegurado',
  liability: 'Actividad asegurada', 'personal-accident': 'Persona asegurada', life: 'Persona asegurada',
};
const RC_LIMIT = 30000000;

function roundTo(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/** Orientative sum insured: the model has no field for it, so it is derived from the premium per ramo. */
export function sumInsuredOf(policy: Pick<Policy, 'oid' | 'premium' | 'lineOfBusiness'>): number {
  const spread = 85 + (hashOf(policy.oid) % 30);
  return roundTo((policy.premium * SUM_FACTOR[policy.lineOfBusiness] * spread) / 100, 100000);
}

function levelOf(policy: Policy): string {
  const levels = COVERAGE_LEVELS[policy.lineOfBusiness];
  return levels[hashOf(policy.oid) % levels.length];
}

function itemLabels(policy: Policy): string[] {
  const h = hashOf(policy.oid);
  if (policy.lineOfBusiness !== 'auto' || h % 9 !== 0) return [policy.subject];
  return [
    policy.subject,
    ...[0, 1].map(n => `${FLEET_VEHICLES[(h + n) % FLEET_VEHICLES.length]} (A${(h + n * 7) % 10}C${100 + ((h >> 3) + n * 13) % 800}ZB)`),
  ];
}

function riskItems(policy: Policy): RiskItem[] {
  const line = policy.lineOfBusiness;
  const level = levelOf(policy);
  const labels = itemLabels(policy);
  const total = sumInsuredOf(policy);
  const each = roundTo(total / labels.length, 100000);
  const h = hashOf(policy.oid);
  const deductible = line === 'auto' && level === 'Todo Riesgo con franquicia' ? [50000, 100000, 150000][h % 3] : null;
  return labels.map(label => ({
    label,
    description: ITEM_DESCRIPTION[line],
    level,
    sumInsured: each,
    deductible,
    coverages: COVERAGE_CATALOG[line].map(def => {
      const included = coverageIncluded(line, level, def);
      return {
        name: def.name,
        included,
        sumInsured: included ? (def.name === 'Responsabilidad civil' ? RC_LIMIT : each) : null,
        deductible: included && deductible !== null && DAMAGE_COVERAGES.has(def.name) ? deductible : null,
      };
    }),
  }));
}

function people(policy: Policy, db: ProtoDb): PolicyPerson[] {
  const person = db.persons.find(p => p.oid === policy.personOid);
  const name = person?.displayName ?? '—';
  const detail = person ? `${person.documentType} ${person.documentNumber}` : '';
  const out: PolicyPerson[] = [
    { role: 'policyholder', name, detail, personOid: person?.oid ?? null },
    { role: 'insured', name, detail: policy.lineOfBusiness === 'life' || policy.lineOfBusiness === 'personal-accident' ? detail : 'Es el mismo tomador', personOid: person?.oid ?? null },
  ];
  if (policy.lineOfBusiness === 'life' || policy.lineOfBusiness === 'personal-accident') {
    const h = hashOf(policy.oid);
    const first = BENEFICIARIES[h % BENEFICIARIES.length];
    const second = BENEFICIARIES[(h + 1) % BENEFICIARIES.length];
    out.push(
      { role: 'beneficiary', name: first, detail: '60 % · Cónyuge o conviviente', personOid: null },
      { role: 'beneficiary', name: second, detail: '40 % · Hijo o hija', personOid: null },
    );
  }
  return out;
}

export function policyExtras(policy: Policy, db: ProtoDb): PolicyExtras {
  return { riskItems: riskItems(policy), people: people(policy, db) };
}
